/**
 * Phase 1 collection-query groupBy / aggregations — FE parsers over the
 * generated wire types, plus FE-only helpers (path filters, request-shape
 * guards) the generated client has no equivalent for.
 *
 * Request extras are spread onto the POST body; response extras are read from
 * the raw envelope. Empty `[]` is omitted, same as facets.
 */

import {
  type CollectionQueryAggregationSpec,
  type CollectionQueryAggregationTotals,
  type CollectionQueryGroupBucket,
  type CollectionQueryGroupTree,
  FilterFieldConditionOp,
  type FilterNode,
} from "~/api/generated/record-service/model";
import { type ColumnAggregationValue, isWeightedAverageAggregation } from "~/domain/viewLayout";
import { getApiErrorMessage, hasApiErrorStatus } from "~/lib/apiClient";

export type CollectionAggregationSpec = CollectionQueryAggregationSpec;

/** Native JSON key from OpenSearch terms; `null` is the missing-value bucket. */
export type CollectionGroupKey = string | number | boolean | null;

export interface CollectionGroupBucket {
  key: CollectionGroupKey;
  count: number;
  aggregations: Record<string, number | null>;
  buckets?: CollectionGroupBucket[];
}

export interface CollectionGroupTree {
  fields: string[];
  truncated: boolean;
  buckets: CollectionGroupBucket[];
}

export interface CollectionQueryTotals {
  count: number;
  aggregations: Record<string, number | null>;
}

export type GroupPathStep = {
  field: string;
  key: CollectionGroupKey;
};

const isGroupKey = (value: unknown): value is CollectionGroupKey =>
  value === null ||
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean";

const parseAggregationMap = (raw: unknown): Record<string, number | null> => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const result: Record<string, number | null> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === null || typeof value === "number") {
      result[id] = value;
    }
  }
  return result;
};

const parseBucket = (raw: unknown): CollectionGroupBucket | null => {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const candidate = raw as CollectionQueryGroupBucket;
  if (!isGroupKey(candidate.key) || typeof candidate.count !== "number") {
    return null;
  }
  const bucket: CollectionGroupBucket = {
    key: candidate.key,
    count: candidate.count,
    aggregations: parseAggregationMap(candidate.aggregations),
  };
  if (Array.isArray(candidate.buckets)) {
    bucket.buckets = candidate.buckets
      .map(parseBucket)
      .filter((child): child is CollectionGroupBucket => child !== null);
  }
  return bucket;
};

/** Pull `groups` off a collection-query envelope; omit when the shape is absent. */
export const parseCollectionGroups = (raw: unknown): CollectionGroupTree | undefined => {
  if (!raw || typeof raw !== "object" || !("groups" in raw)) {
    return undefined;
  }
  const groups = (raw as { groups?: unknown }).groups;
  if (!groups || typeof groups !== "object") {
    return undefined;
  }
  const tree = groups as CollectionQueryGroupTree;
  if (!Array.isArray(tree.fields) || !tree.fields.every((field) => typeof field === "string")) {
    return undefined;
  }
  const buckets = Array.isArray(tree.buckets)
    ? tree.buckets
        .map(parseBucket)
        .filter((bucket): bucket is CollectionGroupBucket => bucket !== null)
    : [];
  return {
    fields: tree.fields,
    truncated: tree.truncated === true,
    buckets,
  };
};

/** Pull `totals` off a collection-query envelope. */
export const parseCollectionTotals = (raw: unknown): CollectionQueryTotals | undefined => {
  if (!raw || typeof raw !== "object" || !("totals" in raw)) {
    return undefined;
  }
  const totals = (raw as { totals?: unknown }).totals;
  if (!totals || typeof totals !== "object") {
    return undefined;
  }
  const candidate = totals as CollectionQueryAggregationTotals;
  if (typeof candidate.count !== "number") {
    return undefined;
  }
  return {
    count: candidate.count,
    aggregations: parseAggregationMap(candidate.aggregations),
  };
};

export interface CollectionQueryGroupFields {
  groupBy?: string[];
  aggregations?: CollectionAggregationSpec[];
}

/**
 * Request extras for POST `/query`. Empty arrays are omitted so the BE treats
 * the call as a plain page (same rule as facets).
 */
export const toCollectionQueryGroupFields = (params: {
  groupBy?: string[];
  aggregations?: CollectionAggregationSpec[];
}): CollectionQueryGroupFields => {
  const extras: CollectionQueryGroupFields = {};
  if (params.groupBy && params.groupBy.length > 0) {
    extras.groupBy = params.groupBy;
  }
  if (params.aggregations && params.aggregations.length > 0) {
    extras.aggregations = params.aggregations;
  }
  return extras;
};

/**
 * Saved view calcs the collection-query API accepts. `sum` / `min` / `max` and
 * weighted average go on the wire. `count` and `avg` stay client-only. A
 * weighted average without a weight field is omitted.
 */
export const toCollectionAggregationSpecs = (
  aggregations: Record<string, ColumnAggregationValue> | undefined,
): CollectionAggregationSpec[] => {
  if (!aggregations) {
    return [];
  }
  const specs: CollectionAggregationSpec[] = [];
  for (const [field, value] of Object.entries(aggregations)) {
    if (value === "sum" || value === "min" || value === "max") {
      specs.push({ id: `${value}_${field}`, func: value, field });
      continue;
    }
    if (isWeightedAverageAggregation(value)) {
      specs.push({
        id: `weightedAvg_${field}`,
        func: "weightedAvg",
        field,
        weightField: value.weightField,
      });
    }
  }
  return specs;
};

const filterForPathStep = (step: GroupPathStep): FilterNode => {
  if (step.key === null) {
    return { field: step.field, op: FilterFieldConditionOp.exists, value: false };
  }
  return { field: step.field, op: FilterFieldConditionOp.eq, value: step.key };
};

/**
 * Narrow a collection filter to one group path. Keys keep their JSON type;
 * a null key is `exists: false`, never the string `"__missing__"`.
 */
export const hasGroupQueryFields = (params: {
  groupBy?: string[];
  aggregations?: CollectionAggregationSpec[];
}): boolean => (params.groupBy?.length ?? 0) > 0 || (params.aggregations?.length ?? 0) > 0;

export const withoutGroupQueryFields = <T extends { groupBy?: unknown; aggregations?: unknown }>(
  params: T,
): Omit<T, "groupBy" | "aggregations"> => {
  const { groupBy: _groupBy, aggregations: _aggregations, ...rest } = params;
  return rest;
};

/**
 * True only when the engine cannot group at all (Postgres / search off).
 * Authoring 400s (unknown field, too many levels, bad agg) must surface.
 */
export const isUnsupportedGroupingError = (error: unknown): boolean => {
  if (!hasApiErrorStatus(error, 400)) {
    return false;
  }
  const message = getApiErrorMessage(error, "");
  return (
    /not supported by engine/i.test(message) ||
    /requires search engine/i.test(message) ||
    /opensearch(?: search)? disabled/i.test(message) ||
    /collection-query.+(?:disabled|not enabled)/i.test(message) ||
    /search is not enabled/i.test(message)
  );
};

export const buildGroupPathFilter = (
  path: readonly GroupPathStep[],
  existingFilter?: FilterNode,
): FilterNode | undefined => {
  const steps = path.map(filterForPathStep);
  if (steps.length === 0) {
    return existingFilter;
  }
  if (existingFilter) {
    return { and: [existingFilter, ...steps] };
  }
  return steps.length === 1 ? steps[0] : { and: steps };
};
